import { TelefonePipe } from '../../../shared/pipes/telefone.pipe';
import { PrazoAtendimento } from '../models/prazo-atendimento.model';
import { EnderecoCanal, UnidadePrazo, textoPrazo } from './models/canal-publico.model';
import { EnderecoPostoPresencial } from './models/posto-presencial-publico.model';

const telefonePipe = new TelefonePipe();

/**
 * O que a página `/ouvidoria` e o atendimento presencial exibem do canal e dos postos, escrito
 * **num lugar só** para que as duas telas digam a mesma coisa.
 *
 * ⚠️ Nenhum dado da Ouvidoria é escrito aqui: endereço, contato, responsável e prazos vêm da API
 * (`GET ouvidoria/publico/canal` e `GET ouvidoria/publico/postos-presenciais`). Não há texto da Lei
 * 13.460/2017 como reserva, e não há recurso, prorrogação nem reabertura: no domínio da Ouvidoria a
 * prorrogação virou **contestação** e a reabertura virou **encaminhamento**, que é prazo interno.
 */

/** O texto da linha *Responsável* quando o backend devolve o nome nulo — o mesmo do e-SIC. */
export const RESPONSAVEL_EM_ATUALIZACAO = 'em atualização';

/** O estado vazio da página `/ouvidoria`: sem canal ativo, ou leitura em falha. */
export const OUVIDORIA_NAO_PUBLICADA = 'O atendimento da Ouvidoria ainda não foi publicado por este órgão.';

/** O estado vazio do presencial: sem posto, ou leitura em falha. */
export const PRESENCIAL_NAO_PUBLICADO = 'Este órgão ainda não publicou atendimento presencial da Ouvidoria.';

/** Os prazos que valem para o cidadão, na unidade do canal. */
export interface PrazosDoCidadao {
  prazoResposta: number;
  permiteContestacao: boolean;
  prazoContestacao?: number | null;
  unidade: UnidadePrazo;
}

/**
 * Resposta sempre; contestação só quando permitida e com prazo. *"Até 30 dias úteis"* e
 * *"Até 10 dias úteis após a resposta"* — o mesmo `textoPrazo` do formulário de manifestação.
 */
export function prazosOuvidoria(prazos: PrazosDoCidadao | null): readonly PrazoAtendimento[] {
  if (!prazos) return [];

  const lista: PrazoAtendimento[] = [
    {
      id: 'resposta',
      rotulo: 'Prazo de resposta',
      icone: 'timer',
      valor: `Até ${textoPrazo(prazos.prazoResposta, prazos.unidade)}`,
    },
  ];

  if (prazos.permiteContestacao && prazos.prazoContestacao) {
    lista.push({
      id: 'contestacao',
      rotulo: 'Prazo de contestação',
      icone: 'scale',
      valor: `Até ${textoPrazo(prazos.prazoContestacao, prazos.unidade)} após a resposta`,
    });
  }

  return lista;
}

/** O nome do responsável, ou *"em atualização"* — a linha é exibida sempre. */
export function responsavelOuvidoria(nome: string | null | undefined): string {
  return nome?.trim() || RESPONSAVEL_EM_ATUALIZACAO;
}

/**
 * O telefone para leitura: `(71) 3000-5000`. O admin grava **só os dígitos** (o `ngx-mask` tira a
 * máscara), então quem formata é a tela; o link de chamada usa só os dígitos.
 */
export function telefoneOuvidoria(telefone: string | null | undefined): string {
  return telefonePipe.transform(limpo(telefone));
}

/** O link de chamada, só com dígitos — vazio sem telefone. */
export function telefoneHref(telefone: string | null | undefined): string {
  const digitos = limpo(telefone).replace(/\D/g, '');

  return digitos ? `tel:${digitos}` : '';
}

/**
 * O endereço do canal para leitura: `Rua X, 285, Térreo — Centro, Rio Branco-AC, CEP: 69900-000`.
 * Parte vazia some junto com o separador dela.
 */
export function enderecoCanal(endereco: EnderecoCanal | null | undefined): string {
  if (!endereco) return '';

  return enderecoLegivel(juntar(', ', endereco.logradouro, endereco.numero, endereco.complemento), endereco);
}

/** O endereço do posto para leitura — a rua é o campo livre do cadastro da ouvidoria. */
export function enderecoPosto(endereco: EnderecoPostoPresencial | null | undefined): string {
  if (!endereco) return '';

  return enderecoLegivel(limpo(endereco.endereco), endereco);
}

/**
 * O endereço do posto como consulta de mapa e de rota, sem rótulo:
 * `Rua X, 285, Centro, Rio Branco - AC, 69900-000`.
 */
export function consultaEnderecoPosto(endereco: EnderecoPostoPresencial | null | undefined): string {
  if (!endereco) return '';

  return juntar(
    ', ',
    endereco.endereco,
    endereco.bairro,
    juntar(' - ', endereco.cidade, endereco.uf),
    cepFormatado(endereco.cep)
  );
}

interface LocalDoEndereco {
  bairro?: string | null;
  cidade?: string | null;
  uf?: string | null;
  cep?: string | null;
}

function enderecoLegivel(rua: string, local: LocalDoEndereco): string {
  const cidade = juntar('-', local.cidade, local.uf);
  const bairroECidade = juntar(', ', local.bairro, cidade);
  const cep = limpo(local.cep) ? `CEP: ${cepFormatado(local.cep)}` : '';

  return juntar(', ', juntar(' — ', rua, bairroECidade), cep);
}

/** O CEP com hífen quando vier só com os 8 dígitos (o admin o grava sem máscara). */
function cepFormatado(cep: string | null | undefined): string {
  const digitos = limpo(cep).replace(/\D/g, '');

  return digitos.length === 8 ? `${digitos.slice(0, 5)}-${digitos.slice(5)}` : limpo(cep);
}

function limpo(valor: string | null | undefined): string {
  return (valor ?? '').trim();
}

function juntar(separador: string, ...partes: (string | null | undefined)[]): string {
  return partes.map(limpo).filter(Boolean).join(separador);
}
